import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ContactDetailProps } from "./types";
import { useTranslate } from "helpers";
import { Grid } from "ui/components/Grid";
import { AvailableWidget, GridRef } from "ui/components/Grid/types";
import { generateGridFromTemplate } from "ui/components/Grid/helper";
import { GridTemplate, GRID_TEMPLATES } from "ui/components/Grid/templates";
import { CONTACT_AVAILABLE_WIDGETS } from "./consts";
import WidgetEditCard from "./components/WidgetEditCard/WidgetEditCard";
import { ContactEmailRef } from "./components/ContactEmailCard/types";
import { ContactPhoneRef } from "./components/ContactPhoneCard/types";
import { ContactAddressRef } from "./components/ContactAddressCard/types";
import { ContactNoteRef } from "./components/ContactNoteCard/types";
import { ContactRepresentativeRef } from "./components/ContactRepresentativeCard/types";

const ContactDetailCard: React.FC<ContactDetailProps> = (props) => {
  const { contactDetail, showAddWidgetModal, setShowAddWidgetModal, onEditContact } = props;
  const { t } = useTranslate();

  const gridRef = useRef<GridRef>(null);
  const emailRef = useRef<ContactEmailRef>(null);
  const phoneRef = useRef<ContactPhoneRef>(null);
  const addressRef = useRef<ContactAddressRef>(null);
  const noteRef = useRef<ContactNoteRef>(null);
  const representativeRef = useRef<ContactRepresentativeRef>(null);

  // Grid state — read once on mount
  const [savedState] = useState(() =>
    JSON.parse(localStorage.getItem(`contact-detail-state`) || "null")
  );
  const lastLayoutsRef = useRef<any>(savedState?.layouts ?? null);

  const initialUserWidgets =
    savedState?.userWidgets ?? CONTACT_AVAILABLE_WIDGETS(t, contactDetail, emailRef, phoneRef, addressRef, noteRef, representativeRef, onEditContact).map((w) => w.id);

  const [userWidgets, setUserWidgets] = useState<string[]>(initialUserWidgets);
  const [template, setTemplate] = useState<GridTemplate>(
    savedState?.template ?? "three-columns"
  );

  const templateConfig = useMemo(
    () => GRID_TEMPLATES.find((tpl) => tpl.id === template) || GRID_TEMPLATES[0],
    [template]
  );

  const { widgets, layouts } = useMemo(() => {
    const availableWidgets = userWidgets
      .map((id) => CONTACT_AVAILABLE_WIDGETS(t, contactDetail, emailRef, phoneRef, addressRef, noteRef, representativeRef, onEditContact).find((w) => w.id === id))
      .filter(Boolean) as AvailableWidget[];

    return generateGridFromTemplate(templateConfig, availableWidgets);
  }, [userWidgets, t, contactDetail, onEditContact, templateConfig]);

  const saveContactState = useCallback(
    (layoutsToSave?: any) => {
      if (layoutsToSave) {
        lastLayoutsRef.current = layoutsToSave;
      }
      localStorage.setItem(
        `contact-detail-state`,
        JSON.stringify({
          userWidgets,
          template,
          layouts: layoutsToSave ?? lastLayoutsRef.current ?? layouts,
        })
      );
    },
    [userWidgets, template, layouts]
  );

  useEffect(() => {
    if (contactDetail.id) saveContactState();
  }, [userWidgets, template, saveContactState, contactDetail.id]);

  const handleTemplateChange = useCallback(
    (newTemplate: GridTemplate) => {
      setTemplate(newTemplate);
      lastLayoutsRef.current = null;
      localStorage.setItem(
        "contact-detail-state",
        JSON.stringify({
          userWidgets,
          template: newTemplate,
          layouts: null,
        })
      );
    },
    [userWidgets]
  );

  const handleEditWidgetSuccess = useCallback(() => {
    setShowAddWidgetModal(false);
  }, []);

  return (
    <>
      <Grid
        key={`${userWidgets.join(",")}-${template}-${contactDetail.id ?? ""}`}
        ref={gridRef}
        initialWidgets={widgets}
        initialLayouts={lastLayoutsRef.current ?? layouts}
        onLayoutChange={(newLayouts) => saveContactState(newLayouts)}
      />

      <WidgetEditCard
        show={showAddWidgetModal}
        availableWidgets={CONTACT_AVAILABLE_WIDGETS(t, contactDetail, emailRef, phoneRef, addressRef, noteRef, representativeRef, onEditContact)}
        userWidgets={userWidgets}
        setUserWidgets={setUserWidgets}
        template={template}
        onTemplateChange={handleTemplateChange}
        onSuccess={handleEditWidgetSuccess}
        onClose={() => setShowAddWidgetModal(false)}
      />
    </>
  );
};
export default memo(ContactDetailCard);
